import { compact, get, isEqual, sortBy } from "lodash-es";
import React, { useCallback } from "react";
import { Field } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Card, CardBody, Col, FormGroup } from "reactstrap";
import { AttachmentsListGroup } from "../components/AttachmentsListGroup";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { FormRow } from "../components/forms/FormRow";
import { CptSelector } from "../components/inputs/final_form/CptSelector";
import { DateInput } from "../components/inputs/final_form/DateInput";
import { DropzoneInput } from "../components/inputs/final_form/DropzoneInput";
import { SerializedIcd10Selector } from "../components/inputs/final_form/Icd10Selector";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { Switch } from "../components/inputs/final_form/Switch";
import { TestSelector } from "../components/inputs/final_form/TestSelector";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { SITE_OF_SERVICE_OPTIONS } from "../system_config/benefit_pull_types/BenefitPullTypePage";
import { composeValidators, required } from "../util/validators";
import { useSelectedTestUpdateFunction } from "./TestAndRequestInfoHelperMethods";

export const TestAndRequestInfoFields = ({ isClinicUser, isRequestingProviderTypePharmacy, requestingProvider, values, form }) => {
  const shouldUpdateLabel = isClinicUser && isRequestingProviderTypePharmacy;
  const showOptionalFields = values.orderedWorkflow === "priorAuthRequest";
  const existingFiles = get(values, "supplementalDocuments.files", []);
  const physicianName = requestingProvider?.name?.split(" ") ?? [];

  const {
    updateTestInfoFromSelectedTestIds,
    error: selectedMappingsError,
    loading: loadingSelectedMappings,
  } = useSelectedTestUpdateFunction(form, shouldUpdateLabel);

  // When the user selects a test identifier or a test name, then we're
  // going to sync both inputs to reflect the same selections. This has
  // the stupid side-effect of sometimes _looking_ like it's not working
  // if the user has loaded a test name that is not loaded in the test identifier
  // input.
  //
  // TODO: This is silly. Why do we have two inputs for the same data. If we want
  // to provide the ability to select or search based on test identifier instead of
  // test name, we should update how the select renders the label, not try to keep
  // duplicative data.
  const syncTestSelects = useCallback(
    async (value) => {
      // Sort all the values so that we can do comparisons
      const sortedValue = sortBy(value);
      const sortedVerifiedIds = sortBy(values.testInfo?.verifiedTestIds ?? []);
      const sortedTestIdentifierIds = sortBy(values.testInfo?.testIdentifierfield ?? []);

      // Only update the form if we actually changed the value
      if (!isEqual(sortedValue, sortedTestIdentifierIds)) {
        form.change("testInfo.testIdentifierfield", value);
      }

      // Only update the form if we actually changed the value
      if (!isEqual(sortedValue, sortedVerifiedIds)) {
        form.change("testInfo.verifiedTestIds", value);
      }
    },
    [form, values]
  );

  return (
    <>
      <h5 className="mb-4">{shouldUpdateLabel ? "Product or Procedure Info" : "Test & Request Info"}</h5>
      <FormGroup>
        <FormRow>
          <Col>
            <Field name="testInfo.dateOfService" render={DateInput} label="Date of Service" data-testid="dateOfService" />
          </Col>
          <Col>
            <Field name="testInfo.collectionDate" render={DateInput} label="Collection Date" data-testid="collectionDate" />
          </Col>
          {showOptionalFields && (
            <Col>
              <Field name="testInfo.externalClinicId" render={TextInput} label="Clinic ID" data-testid="clinicID" />
            </Col>
          )}
        </FormRow>

        <FormRow>
          {showOptionalFields && (
            <>
              <Col>
                <Field name="testInfo.collectionType" render={TextInput} label="Collection Type" data-testid="collectionType" />
              </Col>
              <Col>
                <Field name="requestInfo.accessionDate" component={DateInput} label="Accession Date" data-testid="accessionDate" />
              </Col>
            </>
          )}
          <Col>
            <Field
              name="requestInfo.orderId"
              component={TextInput}
              label={shouldUpdateLabel ? "Order ID" : "Lab Order ID"}
              data-testid="labOrderID"
            />
          </Col>
        </FormRow>

        <Card className="w-100 mb-3 bg-info bg-opacity-10 border border-0">
          <CardBody>
            <FormRow>
              {isClinicUser && !isRequestingProviderTypePharmacy && (
                <Col>
                  <Field id="allTests" component={Switch} label={"Show all tests"} name="allTests" type="checkbox" />
                </Col>
              )}
            </FormRow>
            <FormRow>
              <Col>
                <Col>
                  <Field
                    name="testInfo.verifiedTestIds"
                    validate={composeValidators(required)}
                    component={TestSelector}
                    allTests={values.allTests}
                    labIds={shouldUpdateLabel ? undefined : [values.labId]}
                    resourceUrl={shouldUpdateLabel ? "/tests/clinic_test_mappings" : undefined}
                    label={shouldUpdateLabel ? "Product or Procedure Name" : "Test Name"}
                    data-testid="verifiedTestIds"
                    required
                    isMulti
                  />

                  <DetailedErrorAlert error={selectedMappingsError} />
                </Col>
              </Col>

              {showOptionalFields && (
                <Col>
                  <Field
                    name="testInfo.testIdentifierfield"
                    validate={composeValidators(required)}
                    component={TestSelector}
                    allTests={values.allTests}
                    labIds={shouldUpdateLabel ? undefined : [values.labId]}
                    resourceUrl={shouldUpdateLabel ? "/tests/clinic_test_mappings" : undefined}
                    label="Test Identifier"
                    labelSelector="identifier"
                    value
                    data-testid="testIdentifier"
                    required
                    isMulti
                  />
                </Col>
              )}

              <OnChange name="testInfo.testIdentifierfield">{syncTestSelects}</OnChange>
              <OnChange name="testInfo.verifiedTestIds">{syncTestSelects}</OnChange>
              <OnChange name="testInfo.verifiedTestIds">{updateTestInfoFromSelectedTestIds}</OnChange>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  required
                  validate={composeValidators(required)}
                  name="testInfo.cptCodes"
                  render={CptSelector}
                  label={shouldUpdateLabel ? "Procedure Codes" : "CPT Codes"}
                  data-testid="cptCodes"
                  disabled={loadingSelectedMappings}
                  isPharmacyClinicUser={shouldUpdateLabel}
                  readOnlyCptCodes={shouldUpdateLabel ? true : false}
                />
              </Col>

              <Col>
                <Field
                  required
                  name="testInfo.icd10Codes"
                  validate={composeValidators(required)}
                  render={SerializedIcd10Selector}
                  label="ICD10 Codes"
                  data-testid="icd10Codes"
                  hl7={true}
                />
                <Field
                  label="Supplemental Documents"
                  name="supplementalDocuments.newFiles"
                  component={DropzoneInput}
                  multiple
                  data-testid="supplementalDocuments"
                />
                {existingFiles && existingFiles.length > 0 && (
                  <>
                    <strong className="small text-muted mb-2">Read Only</strong>
                    <AttachmentsListGroup attachments={compact(existingFiles)} />
                  </>
                )}
              </Col>
            </FormRow>
          </CardBody>
        </Card>

        {isClinicUser && isRequestingProviderTypePharmacy && (
          <>
            <Field name="providerInfo.physicianFirstName" component={TextInput} type="hidden" initialValue={physicianName[0]} />
            <Field name="providerInfo.physicianLastName" component={TextInput} type="hidden" initialValue={physicianName[1]} />
            <Field name="providerInfo.physicianNPI" component={TextInput} type="hidden" initialValue={requestingProvider?.npi} />
            <Field
              component={SelectInput}
              label="Site of Service"
              options={SITE_OF_SERVICE_OPTIONS}
              name="providerInfo.siteOfService"
              required
              validate={required}
            />
          </>
        )}
      </FormGroup>
    </>
  );
};
